import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Tenant, Period, Notification, ApiError } from '@/types';
import { authApi, periodsApi } from '@/api/client';
import { AUTH_STORE_KEY, errorStatus, reconcilePersistedAuth } from '@/lib/authSession';

// --- Auth Store --------------------------------------------------------------

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  /** Re-read /auth/me WITHOUT touching isLoading (ProtectedRoute swaps the
   *  page for a spinner while that is true). Used after a silent token
   *  refresh so a role change made in Keycloak reaches menus/route gating
   *  without a reload — and without blanking the screen every 5 minutes. */
  syncUser: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login({ email, password });
          localStorage.setItem('auth_token', response.access_token);
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token);
          }
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const apiError = err as ApiError;
          set({
            isLoading: false,
            error: apiError.detail || 'Login failed. Please check your credentials.',
            isAuthenticated: false,
            user: null,
            token: null,
          });
          throw err;
        }
      },

      logout: () => {
        const provider = localStorage.getItem('auth_provider');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });
        // The landing page is the product's front door and carries the sign-in
        // form, so signing out returns there rather than to a bare form.
        // A full page load, not a router navigation: it also clears the
        // in-memory state of every screen the signed-out user was just on.
        if (provider === 'keycloak') {
          // End the Keycloak session too (RBAC R1) — the realm redirects back
          // to the landing page; on any failure fall through to it directly.
          import('@/lib/keycloakAuth')
            .then((m) => m.keycloakLogout())
            .catch(() => { window.location.href = '/welcome'; });
          return;
        }
        window.location.href = '/welcome';
      },

      syncUser: async () => {
        if (!(get().token || localStorage.getItem('auth_token'))) return;
        try {
          const user = await authApi.getMe();
          set({ user, isAuthenticated: true });
        } catch {
          // Leave state as is — the interceptor already handles a dead session.
        }
      },

      loadUser: async () => {
        const token = get().token || localStorage.getItem('auth_token');
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }
        try {
          set({ isLoading: true });
          const user = await authApi.getMe();
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err) {
          // Only a REJECTED credential ends the session. This used to be a bare
          // `catch` that destroyed the token on any failure at all, which is a
          // very different thing from the server saying "no".
          //
          // `loadUser` runs on every boot, so a plain reload while the API was
          // unreachable — a backend container restarting, a rolling deploy, a
          // dropped VPN, a proxy `ECONNREFUSED` — was read as "your session is
          // invalid". The token was deleted and the user was bounced to the
          // landing page and made to sign in again, with a perfectly good token
          // and nothing actually wrong with their session.
          //
          // 401/403 is the server rejecting the credential: clear it. Anything
          // else (no response at all, 5xx, timeout) says nothing about the
          // token, so the persisted session is kept and the app carries on with
          // the user already restored from storage. The next request retries
          // naturally, and a token that really is dead still gets cleared by the
          // 401 handler in `api/client.ts`.
          // api/client.ts rejects with ApiError ({ status_code }), not an
          // AxiosError — reading `response.status` alone was always undefined,
          // so a rejected token was never cleared (one half of the
          // /welcome ⇄ dashboard redirect loop).
          const status = errorStatus(err);
          if (status === 401 || status === 403) {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            return;
          }
          set({ isLoading: false });
        }
      },

      refreshToken: async () => {
        try {
          const response = await authApi.refreshToken();
          localStorage.setItem('auth_token', response.access_token);
          set({
            user: response.user,
            token: response.access_token,
            isAuthenticated: true,
          });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: AUTH_STORE_KEY,
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Never restore "signed in" without the token requests actually send —
      // see lib/authSession.ts (the /welcome ⇄ dashboard loop).
      merge: (persisted, current) => ({
        ...current,
        ...reconcilePersistedAuth(
          persisted as Partial<AuthState> | undefined,
          (() => {
            try {
              return localStorage.getItem('auth_token');
            } catch {
              return null;
            }
          })(),
        ),
      }),
      // `merge` only fixes the in-memory copy; write the healed state back so
      // localStorage stops saying "signed in" too. Any `set` persists the
      // whole partialized state, and clearError is the one with no effect.
      onRehydrateStorage: () => (state) => {
        try {
          if (state && !state.isAuthenticated && !localStorage.getItem('auth_token')) {
            state.clearError();
          }
        } catch {
          // Storage unavailable: nothing persisted, nothing to heal.
        }
      },
    },
  ),
);

// --- App Store ---------------------------------------------------------------

interface AppState {
  currentTenant: Tenant | null;
  currentPeriod: Period | null;
  periods: Period[];
  periodsLoading: boolean;
  sidebarCollapsed: boolean;
  notifications: Notification[];

  setCurrentTenant: (tenant: Tenant | null) => void;
  setCurrentPeriod: (period: Period | null) => void;
  setPeriods: (periods: Period[]) => void;
  fetchPeriods: () => Promise<void>;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;
}

export const useAppStore = create<AppState>()((set, get) => ({
  currentTenant: null,
  currentPeriod: null,
  periods: [],
  periodsLoading: false,
  sidebarCollapsed: true,
  notifications: [],

  setCurrentTenant: (tenant) => set({ currentTenant: tenant }),

  setCurrentPeriod: (period) => set({ currentPeriod: period }),

  setPeriods: (periods) => set({ periods }),

  fetchPeriods: async () => {
    set({ periodsLoading: true });
    try {
      const periods = await periodsApi.list();
      const currentPeriod = get().currentPeriod;
      // Auto-select the first open period if none is selected
      const openPeriods = periods.filter((p) => p.status === 'open');
      const selectedPeriod = currentPeriod
        ? periods.find((p) => p.id === currentPeriod.id) || openPeriods[0] || periods[0]
        : openPeriods[0] || periods[0];
      set({
        periods,
        currentPeriod: selectedPeriod || null,
        periodsLoading: false,
      });
    } catch {
      set({ periodsLoading: false });
    }
  },

  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  addNotification: (notification) =>
    set((state) => ({
      notifications: [
        {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
          read: false,
        },
        ...state.notifications,
      ].slice(0, 50),
    })),

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  markNotificationRead: (id) =>
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      ),
    })),

  clearNotifications: () => set({ notifications: [] }),
}));
